import React, { useEffect } from 'react'

import { Box, Callout } from '@factorialco/deprecated-design-system'
import { LeaveApprovalRecommendationDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import type Leave from 'modules/timeoff/store/models/Leave'

import { RECOMMENDATION_ACTION, RecommendationAction } from './RecommendationAction'
import {
  RecommendationReason,
  getRecommendationDescriptionRich,
} from './getRecommendationDescriptionRich'

interface Props {
  leave: Leave
  author: string
  onRecommendationData?: (action: RecommendationAction, reason: RecommendationReason | '') => void
}

const LeaveApprovalRecommendation = ({ leave, author, onRecommendationData }: Props) => {
  const { data: recommendationData } = useQuery(LeaveApprovalRecommendationDocument, {
    variables: { leaveId: leave.id },
    skip: !leave.isPending(),
  })

  const getRecommendationText = (action: RecommendationAction) => {
    switch (action) {
      case RECOMMENDATION_ACTION.APPROVE:
        return i18n.t('time_off.approval_recommendation.approval_suggested')
      case RECOMMENDATION_ACTION.REVIEW:
        return i18n.t('time_off.approval_recommendation.review_needed')
      case RECOMMENDATION_ACTION.NO_RECOMMENDATION:
        return i18n.t('time_off.approval_recommendation.not_enough_information')
      default:
        return ''
    }
  }
  const getRecommendationType = (action: RecommendationAction) => {
    switch (action) {
      case RECOMMENDATION_ACTION.APPROVE:
        return 'positive'
      case RECOMMENDATION_ACTION.REVIEW:
        return 'warning'
      case RECOMMENDATION_ACTION.NO_RECOMMENDATION:
        return 'info'
      default:
        return 'info'
    }
  }

  const recommendationActionRaw =
    recommendationData?.timeoff?.leaveApprovalRecommendationsConnection?.nodes?.[0]?.action
  const recommendationAction: RecommendationAction | '' =
    recommendationActionRaw === RECOMMENDATION_ACTION.APPROVE ||
    recommendationActionRaw === RECOMMENDATION_ACTION.REVIEW ||
    recommendationActionRaw === RECOMMENDATION_ACTION.NO_RECOMMENDATION
      ? recommendationActionRaw
      : ''
  const rawReason =
    recommendationData?.timeoff?.leaveApprovalRecommendationsConnection?.nodes?.[0]?.reason

  const recommendationReason: RecommendationReason | '' =
    rawReason && typeof rawReason === 'object' ? rawReason : ''

  useEffect(() => {
    if (onRecommendationData && recommendationAction) {
      onRecommendationData(recommendationAction, recommendationReason)
    }
  }, [recommendationAction, recommendationReason, onRecommendationData])

  if (!leave.isPending()) return null

  if (!recommendationAction) return null

  const recommendationText = getRecommendationText(recommendationAction)
  const recommendationType = getRecommendationType(recommendationAction)
  const employeeName = author
  const recommendationDescription = getRecommendationDescriptionRich(
    recommendationAction,
    recommendationReason,
    employeeName
  )

  return (
    <Box paddingY="s16">
      <Callout
        // @ts-expect-error: description can be ReactNode
        description={recommendationDescription}
        title={recommendationText}
        type={recommendationType}
      />
    </Box>
  )
}

export default LeaveApprovalRecommendation
